import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, Typography, Button, message, Spin } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { unsubscribeFromEmails } from '../api/administratorLeads';

const { Title, Paragraph } = Typography;

const UnsubscribePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [leadName, setLeadName] = useState<string>('');

  const token = searchParams.get('token');
  const email = searchParams.get('email'); // Optional email parameter for enhanced security

  useEffect(() => {
    const handleUnsubscribe = async () => {
      if (!token) {
        setError('Token de desuscripción no válido');
        setLoading(false);
        return;
      }

      try {
        const result = await unsubscribeFromEmails(token, email || undefined);
        if (result.success) {
          setSuccess(true);
          setLeadName(result.leadName || '');
          message.success('Te has dado de baja exitosamente');
        } else {
          setError(result.error || 'Error al procesar la desuscripción');
        }
      } catch (err) {
        setError('Error al procesar la desuscripción');
        console.error('Unsubscribe error:', err);
      } finally {
        setLoading(false);
      }
    };

    handleUnsubscribe();
  }, [token]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        padding: '20px'
      }}>
        <Card style={{ textAlign: 'center', maxWidth: 400, width: '100%' }}>
          <Spin size="large" />
          <Paragraph style={{ marginTop: 16 }}>
            Procesando tu solicitud de desuscripción...
          </Paragraph>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      padding: '20px',
      backgroundColor: '#f5f5f5'
    }}>
      <Card style={{ maxWidth: 500, width: '100%', textAlign: 'center' }}>
        {success ? (
          <>
            <CheckCircleOutlined 
              style={{ 
                fontSize: 64, 
                color: '#52c41a', 
                marginBottom: 16 
              }} 
            />
            <Title level={2} style={{ color: '#52c41a' }}>
              ¡Desuscripción Exitosa!
            </Title>
            <Paragraph>
              {leadName ? `Hola ${leadName},` : 'Hola,'}
            </Paragraph>
            <Paragraph>
              Te has dado de baja exitosamente de nuestras comunicaciones por correo electrónico.
            </Paragraph>
            <Paragraph>
              Ya no recibirás más correos electrónicos de My Apartment App. Tu registro ha sido marcado como "Perdido" en nuestro sistema.
            </Paragraph>
            <Paragraph type="secondary">
              Si cambias de opinión en el futuro, puedes contactarnos en{' '}
              <a href="mailto:support@myapartment.app">support@myapartment.app</a>
            </Paragraph>
          </>
        ) : (
          <>
            <CloseCircleOutlined 
              style={{ 
                fontSize: 64, 
                color: '#ff4d4f', 
                marginBottom: 16 
              }} 
            />
            <Title level={2} style={{ color: '#ff4d4f' }}>
              Error en la Desuscripción
            </Title>
            <Paragraph>
              {error || 'No se pudo procesar tu solicitud de desuscripción.'}
            </Paragraph>
            <Paragraph>
              Por favor, contacta a nuestro equipo de soporte en{' '}
              <a href="mailto:support@myapartment.app">support@myapartment.app</a>
            </Paragraph>
            <Button 
              type="primary" 
              onClick={() => window.location.href = 'https://internal.myapartment.app'}
            >
              Ir a My Apartment App
            </Button>
          </>
        )}
      </Card>
    </div>
  );
};

export default UnsubscribePage;
